import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import {
  ANSWER_MAX,
  ANSWER_MIN,
  BASELINE_WINDOW_DAYS,
  utcToDayNum,
} from "../../../../utils/readiness-report";
import { LegendItem } from "./ReadinessChartParts";
import {
  formatChange,
  isSingleValue,
  sectionTranslator,
} from "./readinessFormat";

// A question that fell by at least this much since the previous period is worth the coach's eye
const DROP_ATTENTION = 0.4;

const SPARK_WIDTH = 200;
const SPARK_HEIGHT = 40;
const SPARK_PAD = 4;
// Past this many days the line is no longer scaled by time: at this size, years of answers would
// pile up into slivers. It becomes the sequence of answers, in at most SPARK_MAX_POINTS blocks.
const SPARK_SEQUENTIAL_FROM_DAYS = 90;
const SPARK_MAX_POINTS = 40;

const tr = sectionTranslator("byQuestion");

/**
 * Gets the tone for the change.
 * @param {*} change
 * @returns
 */
const changeTone = (change) => {
  if (change <= -DROP_ATTENTION) return "down";
  return change >= DROP_ATTENTION ? "up" : "flat";
};

// The runs of days that have a usual range. A run breaks where a day has none (the client was
// learning their normal again) or where the days are further apart than the window the normal
// comes from: joining those would draw a slope the normal never had.
const bandSegments = (points, maxGapDays) => {
  const segments = [];
  let current = [];
  let previous = null;
  for (const point of points) {
    const banded = point.lo != null;
    const gap = previous
      ? utcToDayNum(point.utcDate) - utcToDayNum(previous.utcDate)
      : 0;
    if (!banded || gap > maxGapDays) {
      if (current.length > 1) segments.push(current);
      current = [];
    }
    if (banded) current.push(point);
    previous = point;
  }
  if (current.length > 1) segments.push(current);
  return segments;
};

// The answers of one question as a small line, spread from the first answer of the period to the
// last (not over the whole period, which would squeeze the line into a corner when the client only
// answered for part of it), with the client's usual range
// behind it and an amber dot on each answer below it. Answers are 1 to 5.
// Groups of consecutive answers averaged into one, so a long series fits SPARK_MAX_POINTS
const toBlocks = (series) => {
  if (series.length <= SPARK_MAX_POINTS) return series;
  const size = Math.ceil(series.length / SPARK_MAX_POINTS);
  const blocks = [];
  for (let start = 0; start < series.length; start += size) {
    const chunk = series.slice(start, start + size);
    const last = chunk.at(-1);
    blocks.push({
      utcDate: last.utcDate,
      value: chunk.reduce((sum, p) => sum + p.value, 0) / chunk.length,
      lo: last.lo,
      hi: last.hi,
    });
  }
  return blocks;
};

const spanDays = (series) =>
  utcToDayNum(series.at(-1).utcDate) - utcToDayNum(series[0].utcDate);

const isSequential = (series) => spanDays(series) > SPARK_SEQUENTIAL_FROM_DAYS;

const Sparkline = ({ series }) => {
  const fromDay = utcToDayNum(series[0].utcDate);
  const span = spanDays(series);
  const sequential = isSequential(series);
  const shown = sequential ? toBlocks(series) : series;
  const x = (point, index) => {
    const width = SPARK_WIDTH - 2 * SPARK_PAD;
    if (sequential) {
      return shown.length === 1
        ? SPARK_WIDTH / 2
        : SPARK_PAD + (index / (shown.length - 1)) * width;
    }
    return span === 0
      ? SPARK_WIDTH / 2
      : SPARK_PAD + ((utcToDayNum(point.utcDate) - fromDay) / span) * width;
  };
  const y = (value) =>
    SPARK_HEIGHT -
    SPARK_PAD -
    ((value - ANSWER_MIN) / (ANSWER_MAX - ANSWER_MIN)) *
      (SPARK_HEIGHT - 2 * SPARK_PAD);

  const points = shown.map((point, index) => ({
    ...point,
    px: x(point, index),
    py: y(point.value),
  }));
  const bandPaths = bandSegments(
    points,
    sequential ? Number.POSITIVE_INFINITY : BASELINE_WINDOW_DAYS
  ).map((segment) =>
    [
      ...segment.map((p) => `${p.px},${y(p.hi)}`),
      ...[...segment].reverse().map((p) => `${p.px},${y(p.lo)}`),
    ].join(" ")
  );

  return (
    <svg
      className="rr-spark"
      viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
      aria-hidden="true"
    >
      {bandPaths.map((path) => (
        <polygon key={path} className="rr-spark__band" points={path} />
      ))}
      {[points].map((run) =>
        run.length > 1 ? (
          <polyline
            key={run[0].utcDate}
            className="rr-spark__line"
            points={run.map((p) => `${p.px},${p.py}`).join(" ")}
          />
        ) : (
          <circle
            key={run[0].utcDate}
            className="rr-spark__lone"
            cx={run[0].px}
            cy={run[0].py}
            r={1.5}
          />
        )
      )}
      {points
        .filter((p) => p.lo != null && p.value < p.lo)
        .map((p) => (
          <circle
            key={p.utcDate}
            className="rr-spark__dot"
            cx={p.px}
            cy={p.py}
            r={2.5}
          />
        ))}
    </svg>
  );
};

Sparkline.propTypes = {
  series: PropTypes.array.isRequired,
};

const usuallyText = (t, usual) => {
  if (!usual) return tr(t, "learning");
  return isSingleValue(usual)
    ? tr(t, "usuallyExact", { value: usual.lo })
    : tr(t, "usually", { lo: usual.lo, hi: usual.hi });
};

const QuestionCard = ({ question }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div className={`rr-qcard${question.below ? " rr-qcard--amber" : ""}`}>
      <span className="rr-qcard__label">{tr(t, `labels.${question.key}`)}</span>
      <span className="rr-qcard__value">
        {question.avg == null ? "—" : question.avg.toFixed(1)}
        <span className="rr-qcard__unit">/{ANSWER_MAX}</span>
      </span>
      <span className="rr-qcard__sub">
        {usuallyText(t, question.usual)}
        {question.change == null ? null : (
          <span className={`rr-pill rr-pill--${changeTone(question.change)}`}>
            {formatChange(question.change)}
          </span>
        )}
      </span>
      <Sparkline series={question.series} />
    </div>
  );
};

QuestionCard.propTypes = {
  question: PropTypes.object.isRequired,
};

// What the marks on the cards mean. Only what the cards show is listed: a client still learning
// their normal has no band, no amber dots and no amber card.
const QuestionLegend = ({ questions }) => {
  const { t } = useTranslation("athlete-portal");
  const has = (test) => questions.some(test);
  const items = [
    {
      key: "band",
      show: has((q) => q.usual),
      node: <LegendItem kind="band" label={tr(t, "legend.band")} />,
    },
    {
      key: "dot",
      show: has((q) => q.series.some((p) => p.lo != null && p.value < p.lo)),
      node: <LegendItem kind="amberDot" label={tr(t, "legend.dot")} />,
    },
    {
      key: "card",
      show: has((q) => q.below),
      node: <LegendItem kind="amberEdge" label={tr(t, "legend.card")} />,
    },
    {
      key: "change",
      show: has((q) => q.change != null),
      node: (
        <span className="rr-legend__item">
          <span className="rr-pill rr-pill--down">
            {formatChange(-DROP_ATTENTION)}
          </span>
          <span className="rr-pill rr-pill--up">
            {formatChange(DROP_ATTENTION)}
          </span>
          <span>{tr(t, "legend.change", { threshold: DROP_ATTENTION })}</span>
        </span>
      ),
    },
    {
      key: "blocks",
      show: has((q) => isSequential(q.series)),
      node: <span>{tr(t, "legend.blocks")}</span>,
    },
  ].filter((item) => item.show);
  if (items.length === 0) return null;

  return (
    <div className="rr-legend rr-legend--start">
      {items.map((item) => (
        <span key={item.key} className="contents">
          {item.node}
        </span>
      ))}
    </div>
  );
};

QuestionLegend.propTypes = {
  questions: PropTypes.array.isRequired,
};

export const ReadinessQuestionCards = ({ report }) => (
  <>
    <QuestionLegend questions={report.questions} />
    <div className="rr-qgrid">
      {report.questions.map((question) => (
        <QuestionCard key={question.key} question={question} />
      ))}
    </div>
  </>
);

ReadinessQuestionCards.propTypes = {
  report: PropTypes.shape({
    questions: PropTypes.array.isRequired,
  }).isRequired,
};
